import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Architecture as ArchitectureIcon,
  Height as HeightIcon,
  Apartment as ApartmentIcon,
  Landscape as LandscapeIcon
} from '@mui/icons-material';
import { ZoningAnalysis, BuildableEnvelope } from '../types/api.types';

interface ZoningAnalysisCardProps {
  zoning: ZoningAnalysis;
  envelope: BuildableEnvelope;
}

const ZoningAnalysisCard: React.FC<ZoningAnalysisCardProps> = ({ zoning, envelope }) => {
  const formatArea = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(value);
  };

  const complianceItems = [
    {
      label: 'Lot Size',
      compliant: zoning.compliance?.lot_size ?? true,
      value: `Minimum required: ${formatArea(zoning.min_lot_size || 0)} sq ft`
    },
    {
      label: 'Setbacks',
      compliant: zoning.compliance?.setbacks ?? true,
      value: `Front: ${zoning.min_setbacks?.front || 0}ft, Rear: ${zoning.min_setbacks?.rear || 0}ft, Side: ${zoning.min_setbacks?.side || 0}ft`
    },
    {
      label: 'Current Use',
      compliant: zoning.compliance?.current_use ?? true,
      value: 'Residential use permitted'
    }
  ];

  return (
    <Paper sx={{ p: 4, borderRadius: 3, height: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Zoning Analysis
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`District: ${zoning.district}`}
            color="primary"
            sx={{ fontWeight: 500 }}
          />
          {zoning.special_permits_required?.length > 0 && (
            <Chip
              label="Special Permits Required"
              color="warning"
              size="small"
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <HeightIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {zoning.max_stories || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Max Stories
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {zoning.max_height || 0} ft height
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <ArchitectureIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {((zoning.max_lot_coverage || 0) * 100).toFixed(0)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Max Coverage
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <ApartmentIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {zoning.development_potential?.unit_calculation?.max_units || envelope?.max_units || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Max Units
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <LandscapeIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {((zoning.min_open_space || 0) * 100).toFixed(0)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Min Open Space
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Compliance Status */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Compliance Status
        </Typography>
        <List dense>
          {complianceItems.map((item, index) => (
            <ListItem key={index} sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {item.compliant ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <CancelIcon color="error" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={item.value}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Buildable Envelope */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Buildable Envelope
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Max Building Footprint
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatArea(envelope?.max_footprint || 0)} sq ft
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={75} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Max Gross Floor Area
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatArea(envelope?.max_gfa || 0)} sq ft
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={85} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Allowed Uses */}
      {zoning.allowed_uses?.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Allowed Uses
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {zoning.allowed_uses.slice(0, 5).map((use, index) => (
              <Chip
                key={index}
                label={use}
                size="small"
                variant="outlined"
              />
            ))}
            {zoning.allowed_uses.length > 5 && (
              <Chip
                label={`+${zoning.allowed_uses.length - 5} more`}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ZoningAnalysisCard;